import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganizationMutations } from '../hooks/useOrganizationMutations';
import type { UpdateOrganizationInput, OrganizationSettings as SharedOrganizationSettings } from '@threadheads/shared';
import {
  DEFAULT_PO_SETTINGS,
  resolvePurchaseOrderSettings,
  type PurchaseOrderSettingsData,
} from './settingsShared';

function formatPreviewPo(format: string, prefix: string, cc: string, num: string) {
  const year4 = new Date().getFullYear().toString();
  const year2 = year4.slice(-2);
  return (format || DEFAULT_PO_SETTINGS.numberFormat)
    .replace(/\{PREFIX\}/g, prefix || DEFAULT_PO_SETTINGS.numberPrefix)
    .replace(/\{CC\}/g, cc || 'XX')
    .replace(/\{NUM\}/g, num)
    .replace(/\{YEAR\}/g, year4)
    .replace(/\{YY\}/g, year2);
}

/** The Xero account code's own limit (organization settings schema). */
const JIT_ACCOUNT_CODE_MAX = 20;

export default function PurchaseOrderSettings() {
  const { t } = useTranslation('settings');
  const { organization } = useOrganization();
  const { selectedLocation } = useLocation();
  const { updateOrganization } = useOrganizationMutations();

  const [purchaseOrderSettings, setPurchaseOrderSettings] = useState<PurchaseOrderSettingsData>(() =>
    resolvePurchaseOrderSettings(organization?.settings),
  );
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setPurchaseOrderSettings(resolvePurchaseOrderSettings(organization?.settings));
    setHasChanges(false);
  }, [organization]);

  const handlePoSettingChange = (field: keyof PurchaseOrderSettingsData, value: string | number) => {
    setPurchaseOrderSettings((prev) => ({ ...prev, [field]: value } as PurchaseOrderSettingsData));
    setHasChanges(true);
  };

  const handleReset = () => {
    setPurchaseOrderSettings(resolvePurchaseOrderSettings(organization?.settings));
    setHasChanges(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updates: UpdateOrganizationInput = {};
    const orgSettings: SharedOrganizationSettings = organization?.settings ?? {};
    const currentPoResolved = resolvePurchaseOrderSettings(orgSettings);

    const poChanged =
      purchaseOrderSettings.numberPrefix !== currentPoResolved.numberPrefix ||
      purchaseOrderSettings.numberFormat !== currentPoResolved.numberFormat ||
      purchaseOrderSettings.numberPadding !== currentPoResolved.numberPadding ||
      purchaseOrderSettings.draftPrefix !== currentPoResolved.draftPrefix ||
      purchaseOrderSettings.senderEmail !== currentPoResolved.senderEmail ||
      purchaseOrderSettings.replyToEmail !== currentPoResolved.replyToEmail ||
      purchaseOrderSettings.internalCcEmail !== currentPoResolved.internalCcEmail ||
      purchaseOrderSettings.justInTimeAccountCode.trim() !== currentPoResolved.justInTimeAccountCode;

    if (!poChanged) {
      return;
    }

    updates.settings = {
      ...orgSettings,
      purchaseOrders: {
        // Keep any PO key this form does not edit: the save replaces the object.
        ...orgSettings.purchaseOrders,
        numberPrefix: purchaseOrderSettings.numberPrefix,
        numberFormat: purchaseOrderSettings.numberFormat,
        numberPadding: purchaseOrderSettings.numberPadding,
        draftPrefix: purchaseOrderSettings.draftPrefix,
        senderEmail: purchaseOrderSettings.senderEmail || undefined,
        replyToEmail: purchaseOrderSettings.replyToEmail || undefined,
        internalCcEmail: purchaseOrderSettings.internalCcEmail || undefined,
        justInTimeAccountCode: purchaseOrderSettings.justInTimeAccountCode.trim() || undefined,
      },
    };

    updateOrganization.mutate(updates, {
      onSuccess: () => {
        setHasChanges(false);
      },
    });
  };

  const poPreviewCountryCode = (selectedLocation?.country_code || 'XX').toUpperCase();
  const poPreviewNum = String(1).padStart(
    Math.max(1, Math.min(10, Number(purchaseOrderSettings.numberPadding) || DEFAULT_PO_SETTINGS.numberPadding)),
    '0',
  );
  const poPreviewBase = formatPreviewPo(
    purchaseOrderSettings.numberFormat,
    purchaseOrderSettings.numberPrefix,
    poPreviewCountryCode,
    poPreviewNum,
  );
  const poPreviewDraft = purchaseOrderSettings.draftPrefix
    ? `${purchaseOrderSettings.draftPrefix}-${poPreviewBase}`
    : poPreviewBase;

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Purchase Order Settings</CardTitle>
            <CardDescription>
              Configure how purchase orders are numbered and emailed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h4 className="font-medium mb-4">Numbering Format</h4>

              <div className="mb-4 p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">Preview (next PO number):</p>
                <p className="font-mono text-lg">{poPreviewBase}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Draft preview: <span className="font-mono">{poPreviewDraft}</span>
                </p>
              </div>

              <div className="space-y-2 mb-4">
                <Label htmlFor="po-prefix">Prefix</Label>
                <p className="text-sm text-muted-foreground">
                  Text that appears at the start of PO numbers.
                </p>
                <Input
                  id="po-prefix"
                  value={purchaseOrderSettings.numberPrefix}
                  onChange={(e) => handlePoSettingChange('numberPrefix', e.target.value.toUpperCase())}
                  placeholder={DEFAULT_PO_SETTINGS.numberPrefix}
                  className="w-40 font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="po-format">Format Template</Label>
                <p className="text-sm text-muted-foreground">
                  Use tokens to customize PO number format.
                </p>
                <Input
                  id="po-format"
                  value={purchaseOrderSettings.numberFormat}
                  onChange={(e) => handlePoSettingChange('numberFormat', e.target.value)}
                  placeholder={DEFAULT_PO_SETTINGS.numberFormat}
                  className="font-mono"
                />
                <div className="text-xs text-muted-foreground space-y-1 mt-2">
                  <p><code className="bg-muted px-1 rounded">{'{PREFIX}'}</code> - prefix (e.g. PO)</p>
                  <p><code className="bg-muted px-1 rounded">{'{CC}'}</code> - location country code (e.g. US, AU)</p>
                  <p><code className="bg-muted px-1 rounded">{'{NUM}'}</code> - sequential number</p>
                  <p><code className="bg-muted px-1 rounded">{'{YEAR}'}</code> - 4-digit year</p>
                  <p><code className="bg-muted px-1 rounded">{'{YY}'}</code> - 2-digit year</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Country code uses selected location (<code>{poPreviewCountryCode}</code>) or <code>XX</code> if none selected.
                </p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-medium mb-4">Email Settings</h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="po-sender-email">Sender Email</Label>
                  <p className="text-sm text-muted-foreground">
                    The email address POs are sent from.
                  </p>
                  <Input
                    id="po-sender-email"
                    value={purchaseOrderSettings.senderEmail}
                    onChange={(e) => handlePoSettingChange('senderEmail', e.target.value)}
                    placeholder="po@orders.yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="po-reply-to-email">Reply-To Email</Label>
                  <p className="text-sm text-muted-foreground">
                    Supplier replies are directed to this address.
                  </p>
                  <Input
                    id="po-reply-to-email"
                    value={purchaseOrderSettings.replyToEmail}
                    onChange={(e) => handlePoSettingChange('replyToEmail', e.target.value)}
                    placeholder="accounts@yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="po-internal-cc-email">Internal CC Email</Label>
                  <p className="text-sm text-muted-foreground">
                    Optional: CC this inbox on all PO emails.
                  </p>
                  <Input
                    id="po-internal-cc-email"
                    value={purchaseOrderSettings.internalCcEmail}
                    onChange={(e) => handlePoSettingChange('internalCcEmail', e.target.value)}
                    placeholder="accounts@yourcompany.com"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-medium mb-4">{t('po.jitAccount.heading')}</h4>
              <div className="space-y-2">
                <Label htmlFor="po-jit-account-code">{t('po.jitAccount.label')}</Label>
                <p id="po-jit-account-code-help" className="text-sm text-muted-foreground">
                  {t('po.jitAccount.help')}
                </p>
                <Input
                  id="po-jit-account-code"
                  value={purchaseOrderSettings.justInTimeAccountCode}
                  onChange={(e) => handlePoSettingChange('justInTimeAccountCode', e.target.value)}
                  placeholder={t('po.jitAccount.placeholder')}
                  maxLength={JIT_ACCOUNT_CODE_MAX}
                  autoComplete="off"
                  aria-describedby="po-jit-account-code-help po-jit-account-code-note"
                  className="w-40 font-mono"
                />
                <p id="po-jit-account-code-note" className="text-xs text-muted-foreground">
                  {purchaseOrderSettings.justInTimeAccountCode.trim()
                    ? t('po.jitAccount.set')
                    : t('po.jitAccount.unset')}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-medium mb-4">Threadheads Overrides</h4>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="po-padding">Number Padding</Label>
                  <Input
                    id="po-padding"
                    type="number"
                    min={1}
                    max={10}
                    value={purchaseOrderSettings.numberPadding}
                    onChange={(e) => handlePoSettingChange('numberPadding', Number(e.target.value || 0))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Controls digit width for <code>{'{NUM}'}</code> (e.g. 3 = 001).
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="po-draft-prefix">Draft Prefix</Label>
                  <Input
                    id="po-draft-prefix"
                    value={purchaseOrderSettings.draftPrefix}
                    onChange={(e) => handlePoSettingChange('draftPrefix', e.target.value)}
                    placeholder={DEFAULT_PO_SETTINGS.draftPrefix}
                  />
                  <p className="text-xs text-muted-foreground">
                    Prepended to draft PO numbers. Leave blank to disable.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {hasChanges && (
          <Card>
            <CardContent className="flex items-center justify-between py-4">
              <p className="text-sm text-muted-foreground">
                You have unsaved changes
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={updateOrganization.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateOrganization.isPending}
                >
                  {updateOrganization.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </form>
  );
}
